// Copyright 2025 Sorama B.V.

import { Point3 } from "libraries/DataTypes/Point3";
import { PrpdBinData } from "services/ReportEx/PrpdDataService";

/**
 * This file was created based on the definitions on confluence:
 * https://sorama.atlassian.net/wiki/spaces/SD/pages/3471867978/SorX+data+format
 */

// Common string literal types
export type UnitSystem = "metric" | "imperial" | "unknown";
export type AssetStatus = "undetermined" | "found" | "left";
export type GasType =
    | "other"
    | "air"
    | "argon"
    | "helium"
    | "hydrogen"
    | "nitrogen"
    | "naturalGas"
    | "methane"
    | "oxygen";
export type LinePressureUnit = "Pa" | "psi" | "inH2O" | "bar";
export type LeakType = "Open Orifice" | "Threaded End Cap" | "Quick Connect" | "Hose Slit Cut Leak" | "None";
export type LeakTypeWithoutSpace = "OpenOrifice" | "ThreadedEndCap" | "QuickConnect" | "HoseSlitCutLeak" | "None";
export type OperatingConditions = "DC" | "AC 50 Hz" | "AC 60 Hz";
export type OperatingConditionsWithoutSpace = "DC" | "AC50Hz" | "AC60Hz";
export type ReasonInaccurate = "Sound too loud" | "Sound too soft" | "NA";
export type MeasurementType =
    | "leakDetection"
    | "partialDischarge"
    | "multiband"
    | "mechanicalInspection"
    | "anomalyDetection";

// Human readable mapping for measurement types
export const MeasurementTypeDisplayNames: Record<MeasurementType, string> = {
    leakDetection: "Leak Inspection",
    partialDischarge: "Partial Discharge Inspection",
    multiband: "Multi Band",
    mechanicalInspection: "Mechanical Inspection",
    anomalyDetection: "Anomaly Detection",
};

// Common metadata interface
export type CommonMetadata = {
    dateTime: string;
    estimatedDistance: number;
    estimatedDistanceUnit: string; // typically "meter"
    firmwareRelease: string;
    frequencySelectionFrom: number;
    frequencySelectionFromUnit: string; // typically "Hz"
    frequencySelectionTo: number;
    frequencySelectionToUnit: string; // typically "Hz"
    notes: string;
    sampleRate: number | number[]; // Changed backwards compatibility reasons
    sampleRateUnit: string; // typically "Hz"
    snrValue: number | number[];
    splAtSource: number | number[];
    splAtSourceUnit: string; // typically "dBSPL"
    tags: {
        assetID: string;
        assetName: string;
        assetStatus: AssetStatus;
        assetType: string;
    };
    targetPointDistance: number;
    targetPointDistanceUnit: string; // typically "meter"
    type: MeasurementType;
    version: number;
    sourceLocation?: Point3[];
};

// Leak Detection specific types
export type LeakDetectionSettings = {
    unitSystem: UnitSystem;
    gasCost: number;
    gasCostUnit: string; // example: "EUR / 1000L" | "USD / cubic foot"
    gasType: GasType;
    linePressureUnit: LinePressureUnit;
    linePressureValue: number;
    electricityCost: number;
    electricityCostUnit: string; // example: "EUR / kW" | "USD / kW"
    powerRatio: number;
    powerRatioUnit: string; // example: "kW / 100L / minute" | "kW / cubic foot / minute"
    operatingHoursPerYear: number; // On handheld devices this was `operationHoursPerYear`, we deal with this when parsing
};

export type LeakDetectionResults = {
    unitSystem: UnitSystem;
    type: LeakType | LeakTypeWithoutSpace;
    rate: number | number[];
    rateUnit: string; // example: "1000L / minute" | "cubic foot / minute"

    cost: number;
    costUnit: string; // example: "EUR" | "USD"
    isLeakRateAccurate: boolean | boolean[];
    reasonInaccurate: ReasonInaccurate;

    electricityUsage?: number; // (Was also called powerConsumption in the original spec)
    electricityUsageUnit?: string; // example: "kW / year" (Should be kWh / year? ) (Was called powerConsumptionUnit in the original spec)
};

export type LeakDetectionMetadata = {
    settings: LeakDetectionSettings;
    results: LeakDetectionResults;
};

// Partial Discharge Detection specific types
export type PartialDischargeSettings = {
    operatingConditions: OperatingConditions | OperatingConditionsWithoutSpace;
};

export type PartialDischargeResults = {
    externalPercentage: number;
    internalPercentage: number;
    trackingPercentage: number;
    nonePercentage: number;
    // Note: these four fields together should add up to 100
};

export type PartialDischargeMetadata = {
    settings: PartialDischargeSettings;
    results: PartialDischargeResults;
};

// Multi band specific types (270525, used by cam-iv64-ex)
export type MultibandSettings = {
    baseFrequency: number;
    baseFrequencyUnit: string; // typically "Hz"
};

export type MultibandResults = {
    sourceSnr: number;
    sourceSnrUnit: string; // typically "-"
    splAtSensor: number;
    splAtSensorUnit: string; // typically "dBSPL"
    splBeamformedMin: number;
    splBeamformedMinUnit: string; // typically "dBSPL"
    splBeamformedMax: number;
    splBeamformedMaxUnit: string; // typically "dBSPL"
};

export type MultibandMetadata = {
    settings: MultibandSettings;
    results: MultibandResults;
};

// Mechanical Inspection specific types (270525, used by L642ex, MIv2)
export type MechanicalInspectionResults = {
    sourceSnr: number;
    sourceSnrUnit: string; // typically "-"
    splAtSensor: number;
    splAtSensorUnit: string; // typically "dBSPL"
    splBeamformedMin: number;
    splBeamformedMinUnit: string; // typically "dBSPL"
    splBeamformedMax: number;
    splBeamformedMaxUnit: string; // typically "dBSPL"
};

export type MechanicalInspectionMetadata = {
    results: MechanicalInspectionResults;
};

// Anomaly Detection specific types
export type AnomalyDetectionTrainingSettings = {
    modelPath: string;
    trainingLengthSec: number;
};

export type AnomalyDetectionInferenceSettings = {
    modelPath: string;
    sensitivity: number;
    windowLengthSec: number;
};

export type AnomalyDetectionSettings = {
    trainingSettings: AnomalyDetectionTrainingSettings;
    inferenceSettings: AnomalyDetectionInferenceSettings;
};

export type AnomalyDetectionResults = {
    confidence: number;
    meanScore: number;
    standardDeviationScore: number;
};

export type AnomalyDetectionMetadata = {
    settings: AnomalyDetectionSettings;
    results: AnomalyDetectionResults;
};

// Main SorX metadata type that combines common metadata with specific metadata
export type SorXMetadata = CommonMetadata & {
    ldMetadata?: LeakDetectionMetadata;
    pdMetadata?: PartialDischargeMetadata;
    multibandMetadata?: MultibandMetadata;
    mechanicalInspectionMetadata?: MechanicalInspectionMetadata;
    anomalyDetectionMetadata?: AnomalyDetectionMetadata;
};

// Type guards for determining metadata type
export function isLeakDetectionMetadata(
    metadata: SorXMetadata
): metadata is SorXMetadata & { ldMetadata: LeakDetectionMetadata } {
    return metadata.type === "leakDetection" && metadata.ldMetadata !== undefined;
}

export function isPartialDischargeMetadata(
    metadata: SorXMetadata
): metadata is SorXMetadata & { pdMetadata: PartialDischargeMetadata } {
    return metadata.type === "partialDischarge" && metadata.pdMetadata !== undefined;
}

export function isMultibandMetadata(
    metadata: SorXMetadata
): metadata is SorXMetadata & { multibandMetadata: MultibandMetadata } {
    return metadata.type === "multiband" && metadata.multibandMetadata !== undefined;
}

export function isMechanicalInspectionMetadata(
    metadata: SorXMetadata
): metadata is SorXMetadata & { mechanicalInspectionMetadata: MechanicalInspectionMetadata } {
    return metadata.type === "mechanicalInspection" && metadata.mechanicalInspectionMetadata !== undefined;
}

export function isAnomalyDetectionMetadata(
    metadata: SorXMetadata
): metadata is SorXMetadata & { anomalyDetectionMetadata: AnomalyDetectionMetadata } {
    return metadata.type === "anomalyDetection" && metadata.anomalyDetectionMetadata !== undefined;
}

export type SorXMetadataFull = SorXMetadata & {
    image: string; // Base64 encoded image
    featureImages: string[]; // Array of Base64 encoded images
    prpdBinData?: PrpdBinData; // Optional PRPD data
    rawAudioEncryptionBlob?: Blob; // Encrypted raw audio payload
};
